import React, { useState, forwardRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

interface BaseInputProps {
  label?: string;
  placeholder?: string;
  error?: string;
  success?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'outlined';
}

interface InputProps extends BaseInputProps, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  clearable?: boolean;
  onClear?: () => void;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  placeholder,
  error,
  success,
  hint,
  required,
  disabled,
  className = '',
  size = 'md',
  variant = 'default',
  icon,
  iconPosition = 'left',
  clearable = false,
  onClear,
  value,
  onChange,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm min-h-[36px]',
    md: 'px-4 py-3 text-base min-h-[44px]',
    lg: 'px-5 py-4 text-lg min-h-[52px]'
  };

  const variantClasses = {
    default: 'bg-surface-dark border border-surface-border focus:border-naia-pink/50 focus:bg-surface-raised',
    filled: 'bg-surface-raised border border-transparent focus:border-naia-pink/50',
    outlined: 'bg-transparent border-2 border-surface-border focus:border-naia-pink'
  };

  const stateClasses = error 
    ? 'border-error focus:border-error ring-error/20' 
    : success 
    ? 'border-success focus:border-success ring-success/20'
    : 'focus:ring-naia-pink/20';

  const iconSize = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-text-primary">
          {label}
          {required && <span className="text-naia-pink ml-1">*</span>}
        </label>
      )}
      
      <div className="relative group">
        {icon && iconPosition === 'left' && (
          <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted group-focus-within:text-naia-pink transition-colors ${iconSize}`}>
            {icon}
          </div>
        )}
        
        <input
          ref={ref}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={`
            w-full rounded-lg transition-all duration-200 
            text-text-primary placeholder-text-muted 
            focus:outline-none focus:ring-2 
            disabled:opacity-50 disabled:cursor-not-allowed
            ${sizeClasses[size]}
            ${variantClasses[variant]}
            ${stateClasses}
            ${icon && iconPosition === 'left' ? 'pl-10' : ''}
            ${(icon && iconPosition === 'right') || clearable ? 'pr-10' : ''}
            ${isFocused ? 'elevation-1' : ''}
          `.trim()}
          {...props}
        />
        
        {icon && iconPosition === 'right' && (
          <div className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted group-focus-within:text-naia-pink transition-colors ${iconSize}`}>
            {icon}
          </div>
        )}
        
        {clearable && value && (
          <button
            type="button"
            onClick={onClear}
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors ${iconSize}`}
          >
            ×
          </button>
        )}
        
        {error && (
          <div className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-error ${iconSize}`}>
            <ErrorIcon fontSize="inherit" />
          </div>
        )}
        
        {success && !error && (
          <div className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-success ${iconSize}`}>
            <CheckCircleIcon fontSize="inherit" />
          </div>
        )}
      </div>
      
      {(error || success || hint) && (
        <div className="text-sm space-y-1">
          {error && <p className="text-error flex items-center gap-2"><ErrorIcon fontSize="small" /> {error}</p>}
          {success && !error && <p className="text-success flex items-center gap-2"><CheckCircleIcon fontSize="small" /> {success}</p>}
          {hint && !error && !success && <p className="text-text-muted">{hint}</p>}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

// Search Input Component
interface SearchInputProps extends Omit<InputProps, 'icon' | 'iconPosition'> {
  onSearch?: (value: string) => void;
  loading?: boolean;
}

export function SearchInput({ onSearch, loading, className = '', ...props }: SearchInputProps) {
  const [value, setValue] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    props.onChange?.(e);
    onSearch?.(newValue);
  };

  const handleClear = () => {
    setValue('');
    onSearch?.('');
    props.onClear?.();
  };

  return (
    <Input
      {...props}
      value={value}
      onChange={handleChange}
      onClear={handleClear}
      clearable={value.length > 0}
      icon={loading ? (
        <div className="animate-spin">
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
        </div>
      ) : (
        <SearchIcon />
      )}
      iconPosition="left"
      placeholder={props.placeholder || "Search..."}
      className={`search-input ${className}`}
    />
  );
}

// Password Input Component
interface PasswordInputProps extends Omit<InputProps, 'type' | 'icon' | 'iconPosition'> {
  showStrength?: boolean;
}

export function PasswordInput({ showStrength = false, className = '', ...props }: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    props.onChange?.(e);
  };

  const getPasswordStrength = (pass: string): { score: number; label: string; color: string } => {
    if (!pass) return { score: 0, label: '', color: '' };
    
    let score = 0;
    if (pass.length >= 8) score++;
    if (/[a-z]/.test(pass)) score++;
    if (/[A-Z]/.test(pass)) score++;
    if (/\d/.test(pass)) score++;
    if (/[^a-zA-Z\d]/.test(pass)) score++;

    const strength = {
      0: { label: '', color: '' },
      1: { label: 'Very Weak', color: 'text-red-500' },
      2: { label: 'Weak', color: 'text-orange-500' },
      3: { label: 'Fair', color: 'text-yellow-500' },
      4: { label: 'Good', color: 'text-blue-500' },
      5: { label: 'Strong', color: 'text-green-500' }
    }[score] || { label: '', color: '' };

    return { score, ...strength };
  };

  const strength = getPasswordStrength(password);

  return (
    <div className={className}>
      <Input
        {...props}
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={handleChange}
        icon={
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="text-text-muted hover:text-text-primary transition-colors"
          >
            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </button>
        }
        iconPosition="right"
      />
      
      {showStrength && password && (
        <div className="mt-2 space-y-2">
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((level) => (
              <div
                key={level}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  level <= strength.score 
                    ? strength.score <= 2 ? 'bg-error' : strength.score <= 3 ? 'bg-warning' : 'bg-success'
                    : 'bg-surface-border'
                }`}
              />
            ))}
          </div>
          {strength.label && (
            <p className={`text-xs ${strength.color}`}>
              Password strength: {strength.label}
            </p>
          )}
        </div>
      )}
    </div>
  );
} 